import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import {
  uploadFileAction,
  fetchPatientFolder,
  fetchWorkspace,
  fetchWorkspaceSnapshot,
  fetchTotalStorage,
  createNewFolder,
  renameFolderAction,
  copyFolderAction,
  deleteFolder,
  uploadFilesAction,
  renameFileAction,
  copyFileAction,
  moveFilesAndFoldersAction,
  fetchFileDetails,
  fetchFolderDetails,
  fetchFilePreview,
  fetchFileDownload,
  fetchFolderZipEstimate,
  triggerFolderZipDownload,
  deleteFileAction,
  toggleFileFavoriteAction,
  attachFileToClaimAction,
  attachFileToClaimItemAction,
} from '@store/actions/folder.action'
import type {
  IFolder,
  IFile,
  IWorkspace,
  IWorkspaceFileItem,
} from '@dataTypes/folder.types'
import { useGlobalLoadingActions } from '@hooks'
import { requestPatientSelection } from '@store/reducers/uiSlice.reducer'
import FilesAndLettersHeader, {
  SortConfig,
} from '@components/pages/Files/FilesAndLettersHeader/FilesAndLettersHeader.component'
import FilesAndLettersEmptyState from '@components/pages/Files/FilesAndLettersEmptyState/FilesAndLettersEmptyState.component'
import FilesAndLettersFolderGrid, {
  FolderItem,
  type MixedRootItem,
} from '@components/pages/Files/FilesAndLettersFolderGrid/FilesAndLettersFolderGrid.component'
import FilePreviewPanel from '@components/pages/Files/FilePreviewPanel/FilePreviewPanel.component'
import MoveFileModal, {
  ROOT_FOLDER_ID,
} from '@components/pages/Files/MoveFileModal/MoveFileModal.component'
import AttachToClaimsModal, {
  type ClaimRow,
  type ProcedureRow,
  type ProcedureItemRow,
} from '@components/pages/Files/AttachToClaimsModal/AttachToClaimsModal.component'
import FileDetailsModal from '@components/pages/Files/FileDetailsModal/FileDetailsModal.component'
import FolderDetailsModal from '@components/pages/Files/FolderDetailsModal/FolderDetailsModal.component'
import FilesDeleteConfirmModal from '@components/pages/Files/FilesDeleteConfirmModal.component'
import { getFileTypeFromFileName } from '@components/pages/Files/filesAndLetters.util'
import FilesUploadModal, {
  type FileUpload,
} from '@components/pages/Files/FilesUploadModal/FilesUploadModal.component'
import type { FilterSelection } from '@enamelai/design-system'
import { useNotificationToast } from '@context/NotificationToastContext'
import {
  createSuccessToast,
  createFailedToast,
  createFileMovedToast,
  createMultipleFilesMovedToast,
  createFoldersCopiedToast,
  createRenamedSuccessfullyToast,
  createFileAttachedToClaimToast,
} from '@utils/notificationToast.util'
import type { MockFileItem } from '@pages/Files/mockFilesData'

import { deleteOrphanDicomStudy } from '@services/api/folder.api'
import { getCloudStorageSummary } from '@services/api/cloudStorage/cloud-storage.api'
import { fetchClaims } from '@store/actions/claims.action'
import { fetchAllNotes } from '@store/actions/note.action'

import { NoteContextType, type INote } from '@dataTypes/note.type'
import {
  useNoteEditorOverlay,
  getBackButtonLabelForOrigin,
} from '@components/common/NoteEditorOverlay'
import { logger } from '@utils/safeLogger'

import './Files.page.css'

/** Derive display type from file name for filter. */
function getFileType(fileName: string): string {
  const ext = fileName.split('.').pop()?.toLowerCase() ?? ''
  if (ext === 'pdf') return 'PDF'
  if (ext === 'dcm') return 'X-Ray'
  if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) return 'Image'
  if (['doc', 'docx'].includes(ext)) return 'Document'
  return 'Other'
}

const FILE_TYPE_FILTER_OPTIONS = ['PDF', 'Image', 'Document', 'X-Ray', 'Other']

/** Same context-id resolution used by notes feature internals. */
function getNoteContextItemIdFromNote(note: INote): string {
  const direct = note.context_id?.trim()
  if (direct) return direct
  const details = note.context_details ?? {}
  const appointmentId =
    typeof details.appointment_id === 'string'
      ? details.appointment_id.trim()
      : ''
  if (appointmentId) return appointmentId
  const chartId =
    typeof details.chart_id === 'string' ? details.chart_id.trim() : ''
  if (chartId) return chartId
  const tpId = typeof details.tp_id === 'string' ? details.tp_id.trim() : ''
  return tpId
}

function patientHasFileContextNotes(
  notes: INote[],
  patientId: string,
  fileId: string,
): boolean {
  const normalizedType = NoteContextType.FILES.toString().toLowerCase().trim()
  const normalizedFileId = fileId.trim()
  return notes.some((note) => {
    if (note.patient_id != null && note.patient_id !== patientId) return false
    const noteType = (note.context_type ?? '').toString().toLowerCase().trim()
    if (noteType !== normalizedType) return false
    return getNoteContextItemIdFromNote(note) === normalizedFileId
  })
}

const LOCAL_DRAFT_FOLDER_ID_PREFIX = 'local-draft-folder:'

function isLocalDraftFolderId(id: string): boolean {
  return id.startsWith(LOCAL_DRAFT_FOLDER_ID_PREFIX)
}

function isLocalDraftFolder(folder: FolderItem): boolean {
  return Boolean(folder.isLocalDraft) || isLocalDraftFolderId(folder.id)
}

/**
 * Fallback capacity used only until the tenant's subscribed limit is fetched
 * (or if that fetch fails). The real limit comes from the subscription via
 * `getCloudStorageSummary()` so this bar matches the Settings → Cloud storage
 * page (plan §6.1).
 */
const DEFAULT_STORAGE_LIMIT_BYTES = 5 * 1024 * 1024 * 1024

const formatBytesToLabel = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  if (bytes < 1024 * 1024 * 1024)
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`
}

const buildChildFolderPath = (
  parentPath: string | undefined,
  patientId: string,
  folderName: string,
): string => {
  const fallbackRoot = `/${patientId}`
  const base = (parentPath || fallbackRoot).replace(/\/+$/, '')
  return `${base}/${folderName}`
}

const getNextFolderName = (
  folders: FolderItem[],
  baseName: string,
  parentFolderId: string | null,
): string => {
  const peerNames = new Set(
    folders
      .filter((f) =>
        parentFolderId ? f.parentId === parentFolderId : !f.parentId,
      )
      .map((f) => f.name),
  )
  let nextName = baseName
  let counter = 1
  while (peerNames.has(nextName)) {
    nextName = `${baseName}(${counter})`
    counter += 1
  }
  return nextName
}

const mapWorkspaceFileToMock = (file: IWorkspaceFileItem): MockFileItem => ({
  id: file.file_id,
  fileName: file.file_name || file.original_name,
  fileSize: formatBytesToLabel(file.file_size || 0),
  uploadDate: file.date_time_created_at || new Date().toISOString(),
  isFavorite: !!file.is_favorite,
  study_uid: file.study_uid,
  series_uid: file.series_uid,
  sop_uid: file.sop_uid,
  // Mirror snake_case DICOM UIDs into the camelCase fields the rest of the
  // page (handleFileViewWithDicom, uploadImageForDicom, navigation state)
  // reads from. Without this, a real-DB DICOM file would not be recognised
  // as already-converted and would fail to open in the OHIF viewer.
  studyUid: file.study_uid ?? undefined,
  seriesUid: file.series_uid ?? undefined,
  sopUid: file.sop_uid ?? undefined,
  // Needed by handleFileViewWithDicom to (a) detect an already-converted
  // DICOM sibling for the same source image so we don't create a duplicate
  // .dcm on every click, and (b) place the converted .dcm in the same
  // folder as the source JPG/PNG.
  originalName: file.original_name,
  folderId: file.folder_id,
  isDicom:
    file.file_type === 'dicom' ||
    file.mime_type === 'application/dicom' ||
    Boolean(file.study_uid),
})

const downloadBlobFile = (blob: Blob, fileName: string): void => {
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = fileName
  document.body.appendChild(anchor)
  anchor.click()
  document.body.removeChild(anchor)
  URL.revokeObjectURL(url)
}

function applySortToFiles(
  files: MockFileItem[],
  sortConfig: SortConfig,
): MockFileItem[] {
  const dir = sortConfig.sortDirection === 'zToA' ? -1 : 1
  const cmp =
    sortConfig.sortBy === 'name'
      ? (a: MockFileItem, b: MockFileItem) =>
          a.fileName.localeCompare(b.fileName, undefined, {
            sensitivity: 'base',
          }) * dir
      : sortConfig.sortBy === 'dateModified'
        ? (a: MockFileItem, b: MockFileItem) =>
            (new Date(a.uploadDate).getTime() -
              new Date(b.uploadDate).getTime()) *
            dir
        : (a: MockFileItem, b: MockFileItem) =>
            getFileType(a.fileName).localeCompare(
              getFileType(b.fileName),
              undefined,
              {
                sensitivity: 'base',
              },
            ) * dir
  return [...files].sort(cmp)
}

function sortMixedRootItems(
  items: MixedRootItem[],
  sortConfig: SortConfig,
): MixedRootItem[] {
  const dir = sortConfig.sortDirection === 'zToA' ? -1 : 1
  const sortKey = (item: MixedRootItem): string | number =>
    item.type === 'folder'
      ? sortConfig.sortBy === 'name'
        ? item.folder.name
        : sortConfig.sortBy === 'dateModified'
          ? 0
          : 'Folder'
      : sortConfig.sortBy === 'name'
        ? item.file.fileName
        : sortConfig.sortBy === 'dateModified'
          ? new Date(item.file.uploadDate).getTime()
          : getFileType(item.file.fileName)
  return [...items].sort((a, b) => {
    const ka = sortKey(a)
    const kb = sortKey(b)
    if (typeof ka === 'number' && typeof kb === 'number') return (ka - kb) * dir
    return (
      String(ka).localeCompare(String(kb), undefined, { sensitivity: 'base' }) *
      dir
    )
  })
}

const FILES_LOADING_CONTAINER = '.page-content'
const CLAIMS_ATTACHMENT_ENABLED = true

type FilesPageProps = {
  /** When true, shows Open button for selecting files to attach **/
  selectionMode?: boolean
  /** Called when user clicks Open with selected files */
  onFilesSelected?: (files: MockFileItem[]) => void
  /** Called when user cancels selection (e.g. clicks Cancel) */
  onSelectionCancel?: () => void
  /**
   * When rendered inside another page (e.g. the cloud-storage picker modal
   * opened from the X-ray viewer), auto-open the named folder so the user
   * lands directly on the relevant file list (e.g. "X-Ray Files").
   */
  initialOpenFolderName?: string
  /**
   * When true and the user clicks Open with image (JPG/PNG) selections
   * that lack DICOM UIDs, run the existing `uploadImageForDicom` flow
   * for each — re-using a previously-converted .dcm if one exists, or
   * creating one on the fly — before invoking `onFilesSelected`. This
   * mirrors the behaviour of the Files page's own "View with DICOM"
   * multi-select action so the X-ray viewer modal can open mixed
   * JPG/PNG/DCM selections as proper DICOM viewports.
   */
  convertImagesToDicom?: boolean
}

interface FilesLocationState {
  openFolder?: string
  /** Phase 7.2.3 — when the Charting X-ray panel sends the user here it sets
   *  `from: 'charting-xray-panel'` so the page swaps into select-xray mode
   *  with an `Open` CTA that returns the chosen file to `/charting`. */
  from?: 'charting-xray-panel'
  patientId?: string
  defaultFolderId?: string
  mode?: 'select-xray'
  accept?: string[]
  /** Charting tab path to return to after Open/Cancel (e.g. `/charting`
   *  or `/charting/treatment-plan`). Defaults to `/charting`. */
  returnTo?: string
}

const Files: React.FC<FilesPageProps> = ({
  selectionMode = false,
  onFilesSelected,
  onSelectionCancel,
  initialOpenFolderName,
  convertImagesToDicom = false,
}) => {
  const dispatch = useAppDispatch()
  const location = useLocation()
  const navigate = useNavigate()
  const { openNoteEditor } = useNoteEditorOverlay()
  const selectedPatient = useAppSelector(
    (state) => state.patient.selectedPatient,
  )
  const notesData = useAppSelector((state) => state.note.data)
  const activeClinicId = useAppSelector(
    (state) => state.clinic.selectedClinic?.clinic_id,
  )
  const folderState = useAppSelector((state) => state.folder)
  const workspace = folderState.workspace as IWorkspace | null
  const { selectedFolder, childFolders } = useAppSelector(
    (state) => state.folder,
  )
  const { showToast } = useNotificationToast()
  const { showLoading, hideLoading } = useGlobalLoadingActions()

  const xrayFolderId = useMemo(() => {
    const xrayFolder = childFolders.find(
      (f) => f.folder_name.toLowerCase() === 'x-ray files',
    )
    return xrayFolder?.folder_id ?? selectedFolder?.folder_id
  }, [childFolders, selectedFolder])

  const [searchTerm, setSearchTerm] = useState('')
  const [storageLimitBytes, setStorageLimitBytes] = useState(
    DEFAULT_STORAGE_LIMIT_BYTES,
  )
  const [searchFilters, setSearchFilters] = useState<FilterSelection>({})
  const [showStarred, setShowStarred] = useState(false)
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid')
  const [customFolders, setCustomFolders] = useState<FolderItem[]>([])
  /** Folders created in the UI that are not persisted until the user confirms a name. */
  const [localDraftCustomFolders, setLocalDraftCustomFolders] = useState<
    FolderItem[]
  >([])
  const [localDraftMoveModalFolders, setLocalDraftMoveModalFolders] = useState<
    FolderItem[]
  >([])
  const [openFolder, setOpenFolder] = useState<FolderItem | null>(null)
  const lastWorkspacePatientIdRef = useRef<string | undefined>(undefined)
  const [selectedFileIds, setSelectedFileIds] = useState<string[]>([])

  // Phase 7.2.3 — when the Charting X-ray panel sent us here, run in
  // single-select "Open" mode and return the chosen file to /charting.
  // Captured once on mount so subsequent navigations within /files don't
  // re-enter the mode.
  const [chartingXraySelectMode, setChartingXraySelectMode] =
    useState<FilesLocationState | null>(() => {
      const state = (location.state || null) as FilesLocationState | null
      return state?.from === 'charting-xray-panel' &&
        state?.mode === 'select-xray'
        ? state
        : null
    })
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    sortBy: 'name',
    sortDirection: 'none',
    folders: 'onTop',
  })
  const [previewFile, setPreviewFile] = useState<MockFileItem | null>(null)
  const [isPreviewOpen, setIsPreviewOpen] = useState(false)
  const [isPreviewFullscreen, setIsPreviewFullscreen] = useState(false)
  const [filesByFolder, setFilesByFolder] = useState<
    Record<string, MockFileItem[]>
  >({})
  const [sampleFiles, setSampleFiles] = useState<MockFileItem[]>([])
  const [renamingId, setRenamingId] = useState<string | null>(null)
  const [moveModal, setMoveModal] = useState<{
    show: boolean
    itemNames: string[]
    type: 'file' | 'folder'
    itemId: string
    itemIds?: string[]
    folderIds?: string[]
  }>({ show: false, itemNames: [], type: 'file', itemId: '' })
  const [detailsFile, setDetailsFile] = useState<MockFileItem | null>(null)
  const [detailsFolder, setDetailsFolder] = useState<FolderItem | null>(null)
  const [deleteConfirm, setDeleteConfirm] = useState<{
    show: boolean
    type: 'file' | 'folder'
    ids: string[]
    name: string
  }>({ show: false, type: 'file', ids: [], name: '' })
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [attachToClaimsFileId, setAttachToClaimsFileId] = useState<
    string | null
  >(null)
  const claimsData = useAppSelector((state) => state.claims.data)
  const [moveModalRenamingFolderId, setMoveModalRenamingFolderId] = useState<
    string | null
  >(null)
  const [moveModalFolders, setMoveModalFolders] = useState<FolderItem[] | null>(
    null,
  )
  const [moveModalFoldersLoading, setMoveModalFoldersLoading] = useState(false)
  const [moveModalBrowseScope, setMoveModalBrowseScope] =
    useState<string>(ROOT_FOLDER_ID)
  const filesNotesBackLabel = useMemo(
    () =>
      getBackButtonLabelForOrigin({
        path: location.pathname || '/files',
      }),
    [location.pathname],
  )

  const defaultFolders = useMemo<FolderItem[]>(() => {
    if (!workspace) return []
    return workspace.folders
      .filter((folder) => folder.is_default)
      .map((folder) => ({
        id: folder.folder_id,
        name: folder.folder_name,
        isDefault: true,
        path: folder.path,
      }))
  }, [workspace])
  const currentFolderFiles = useMemo(
    () => (openFolder ? (filesByFolder[openFolder.id] ?? []) : []),
    [openFolder, filesByFolder],
  )

  const filteredCurrentFolderFiles = useMemo(() => {
    let list = showStarred
      ? currentFolderFiles.filter((f) => f.isFavorite)
      : currentFolderFiles
    const term = searchTerm.toLowerCase().trim()
    if (term) {
      list = list.filter((f) => f.fileName.toLowerCase().includes(term))
    }
    const typeFilters = searchFilters.type
    if (typeFilters && typeFilters.length > 0) {
      list = list.filter((f) => typeFilters.includes(getFileType(f.fileName)))
    }
    return list
  }, [currentFolderFiles, showStarred, searchTerm, searchFilters])

  const filteredSampleFiles = useMemo(() => {
    let list = showStarred
      ? sampleFiles.filter((f) => f.isFavorite)
      : sampleFiles
    const term = searchTerm.toLowerCase().trim()
    if (term) {
      list = list.filter((f) => f.fileName.toLowerCase().includes(term))
    }
    const typeFilters = searchFilters.type
    if (typeFilters && typeFilters.length > 0) {
      list = list.filter((f) => typeFilters.includes(getFileType(f.fileName)))
    }
    return list
  }, [sampleFiles, showStarred, searchTerm, searchFilters])

  const sortedCurrentFolderFiles = useMemo(
    () => applySortToFiles(filteredCurrentFolderFiles, sortConfig),
    [filteredCurrentFolderFiles, sortConfig],
  )

  const sortedSampleFiles = useMemo(
    () => applySortToFiles(filteredSampleFiles, sortConfig),
    [filteredSampleFiles, sortConfig],
  )

  const mergedCustomFolders = useMemo(
    () => [...customFolders, ...localDraftCustomFolders],
    [customFolders, localDraftCustomFolders],
  )

  const allVisibleFiles = useMemo(
    () => (openFolder ? sortedCurrentFolderFiles : sortedSampleFiles),
    [openFolder, sortedCurrentFolderFiles, sortedSampleFiles],
  )

  const mixedRootItems = useMemo((): MixedRootItem[] | null => {
    if (openFolder || sortConfig.folders !== 'mixedWithFiles') return null
    // Only show top-level folders in mixed root view
    const topLevelCustomFolders = mergedCustomFolders.filter((f) => !f.parentId)
    const folderItems: MixedRootItem[] = [
      ...defaultFolders.map((f) => ({ type: 'folder' as const, folder: f })),
      ...topLevelCustomFolders.map((f) => ({
        type: 'folder' as const,
        folder: f,
      })),
    ]
    const fileItems: MixedRootItem[] = sortedSampleFiles.map((f) => ({
      type: 'file' as const,
      file: f,
    }))
    return sortMixedRootItems([...folderItems, ...fileItems], sortConfig)
  }, [
    openFolder,
    sortConfig,
    mergedCustomFolders,
    defaultFolders,
    sortedSampleFiles,
  ])

  const workspaceBreadcrumbFolders = useMemo<FolderItem[]>(() => {
    if (
      !openFolder ||
      !workspace?.breadcrumbs ||
      workspace.breadcrumbs.length === 0
    ) {
      return []
    }
    const mapped: FolderItem[] = workspace.breadcrumbs.map(
      (crumb, index, arr) => ({
        id: crumb.folder_id,
        name: crumb.folder_name,
        isDefault: defaultFolders.some((f) => f.id === crumb.folder_id),
        parentId: index > 0 ? arr[index - 1].folder_id : undefined,
        path: crumb.path,
      }),
    )
    if (!mapped.some((item) => item.id === openFolder.id)) {
      mapped.push({
        ...openFolder,
        parentId:
          mapped.length > 0
            ? mapped[mapped.length - 1].id
            : openFolder.parentId,
      })
    }
    return mapped
  }, [openFolder, workspace?.breadcrumbs, defaultFolders])

  // Forward-declared via ref so `handleOpenSelection` can call into
  // `uploadImageForDicom` even though that function is defined further
  // down in the component body. Without this we'd hit a TDZ
  // ReferenceError on render (the useCallback dependency array is
  // evaluated top-to-bottom, before uploadImageForDicom exists).
  type UploadImageForDicomFn = (
    file: MockFileItem,
  ) => Promise<{ studyUid: string; seriesUid: string } | null>
  const uploadImageForDicomRef = useRef<UploadImageForDicomFn | null>(null)

  const handleOpenSelection = useCallback((): void => {
    if (selectionMode && onFilesSelected) {
      const selectedFiles = allVisibleFiles.filter((f) =>
        selectedFileIds.includes(f.id),
      )
      if (!convertImagesToDicom) {
        onFilesSelected(selectedFiles)
        return
      }
      // Mixed JPG/PNG/DCM selection — resolve every non-DICOM image to a
      // converted .dcm first (re-using an existing converted file if one
      // already exists for the same source name, otherwise generating a
      // new one via the backend). Mirrors `handleFileViewWithDicomMultiple`
      // but routes the resolved files back through `onFilesSelected`
      // instead of navigating away.
      const needsConversion = selectedFiles.filter(
        (f) => !(f.studyUid ?? f.study_uid),
      )
      const runConversion = async (): Promise<void> => {
        const resolvedById = new Map<
          string,
          { studyUid: string; seriesUid: string }
        >()
        if (needsConversion.length > 0) {
          showLoading({ container: FILES_LOADING_CONTAINER })
          const convert = uploadImageForDicomRef.current
          if (convert) {
            for (const img of needsConversion) {
              const uids = await convert(img)
              if (uids) resolvedById.set(img.id, uids)
            }
          }
          if (selectedPatient?.patient_id) {
            await dispatch(fetchPatientFolder(selectedPatient.patient_id))
          }
          hideLoading(FILES_LOADING_CONTAINER)
        }
        const merged: MockFileItem[] = selectedFiles.map((f) => {
          const fresh = resolvedById.get(f.id)
          if (!fresh) return f
          return {
            ...f,
            studyUid: fresh.studyUid,
            seriesUid: fresh.seriesUid,
            study_uid: fresh.studyUid,
            series_uid: fresh.seriesUid,
          }
        })
        onFilesSelected(merged)
      }
      void runConversion()
      return
    }
    if (chartingXraySelectMode && selectedFileIds.length >= 1) {
      // Preserve the user's selection order so the multi-viewport layout
      // matches the cards they ticked. The Files page tracks IDs only;
      // resolve each to a MockFileItem and forward all of them.
      const filesById = new Map(allVisibleFiles.map((f) => [f.id, f]))
      const resolvedFiles = selectedFileIds
        .map((id) => filesById.get(id))
        .filter((f): f is (typeof allVisibleFiles)[number] => Boolean(f))
      if (resolvedFiles.length === 0) return

      // Reuse the same DICOM resolution the main X-ray viewer flow relies on:
      // a JPG/PNG opens its already-converted .dcm when one exists, otherwise
      // `uploadImageForDicom` converts it on the fly before we hand the
      // study/series UIDs back to the Charting X-ray panel.
      const needsConversion = resolvedFiles.filter(
        (f) => !(f.studyUid ?? f.study_uid),
      )
      const runOpen = async (): Promise<void> => {
        const resolvedById = new Map<
          string,
          { studyUid: string; seriesUid: string }
        >()
        if (needsConversion.length > 0) {
          showLoading({ container: FILES_LOADING_CONTAINER })
          const convert = uploadImageForDicomRef.current
          if (convert) {
            for (const img of needsConversion) {
              const uids = await convert(img)
              if (uids) resolvedById.set(img.id, uids)
            }
          }
          if (selectedPatient?.patient_id) {
            await dispatch(fetchPatientFolder(selectedPatient.patient_id))
          }
          hideLoading(FILES_LOADING_CONTAINER)
        }
        const selectedFiles = resolvedFiles
          .map((file) => {
            const fresh = resolvedById.get(file.id)
            return {
              filename: file.fileName,
              studyInstanceUID:
                fresh?.studyUid ?? file.studyUid ?? file.study_uid ?? undefined,
              seriesInstanceUID:
                fresh?.seriesUid ??
                file.seriesUid ??
                file.series_uid ??
                undefined,
            }
          })
          .filter((f) => Boolean(f.studyInstanceUID))
        if (selectedFiles.length === 0) {
          showToast(
            createFailedToast(
              'Unable to Open',
              'Could not prepare the selected file(s) for the X-ray viewer. Please try again.',
            ),
          )
          return
        }
        navigate(chartingXraySelectMode?.returnTo || '/charting', {
          state: {
            xrayPanelOpen: true,
            selectedFile: selectedFiles[0],
            selectedFiles,
          },
        })
      }
      void runOpen()
    }
  }, [
    selectionMode,
    onFilesSelected,
    allVisibleFiles,
    selectedFileIds,
    chartingXraySelectMode,
    navigate,
    convertImagesToDicom,
    showLoading,
    hideLoading,
    dispatch,
    selectedPatient?.patient_id,
    showToast,
  ])

  const handleChartingXrayCancel = useCallback((): void => {
    const returnTo = chartingXraySelectMode?.returnTo || '/charting'
    setChartingXraySelectMode(null)
    navigate(returnTo, { state: { xrayPanelOpen: true } })
  }, [navigate, chartingXraySelectMode?.returnTo])

  // Effective selection-mode props — either passed in via prop (legacy
  // report flow) or driven by the Charting X-ray route state.
  const effectiveSelectionMode = selectionMode || !!chartingXraySelectMode
  const effectiveOnSelectionCancel = selectionMode
    ? onSelectionCancel
    : chartingXraySelectMode
      ? handleChartingXrayCancel
      : undefined

  const previewIndex = useMemo(
    () =>
      previewFile
        ? allVisibleFiles.findIndex((f) => f.id === previewFile.id)
        : -1,
    [previewFile, allVisibleFiles],
  )

  const handlePreviewPrev = useCallback((): void => {
    if (previewIndex > 0) {
      setPreviewFile(allVisibleFiles[previewIndex - 1])
    }
  }, [previewIndex, allVisibleFiles])

  const handlePreviewNext = useCallback((): void => {
    if (previewIndex < allVisibleFiles.length - 1) {
      setPreviewFile(allVisibleFiles[previewIndex + 1])
    }
  }, [previewIndex, allVisibleFiles])

  useEffect(() => {
    if (!previewFile?.id || previewFile.previewUrl) return
    let cancelled = false
    void dispatch(fetchFilePreview(previewFile.id)).then((response) => {
      if (cancelled) return
      if (!fetchFilePreview.fulfilled.match(response)) return
      const payload = response.payload
      if ('viewer_url' in payload && payload.viewer_url) {
        window.open(payload.viewer_url, '_blank', 'noopener,noreferrer')
        return
      }
      if ('url' in payload && payload.url) {
        const previewUrl = payload.url
        setPreviewFile((prev) =>
          prev && prev.id === previewFile.id ? { ...prev, previewUrl } : prev,
        )
      }
    })
    return () => {
      cancelled = true
    }
  }, [dispatch, previewFile?.id, previewFile?.previewUrl])

  useEffect(() => {
    if (selectedPatient?.patient_id) {
      dispatch(fetchPatientFolder(selectedPatient.patient_id))
    }
  }, [dispatch, selectedPatient?.patient_id])

  // const loadingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // useEffect(() => {
  //   if (!workspace) {
  //     showLoading({ container: FILES_LOADING_CONTAINER })
  //   } else {
  //     if (loadingTimeoutRef.current) {
  //       clearTimeout(loadingTimeoutRef.current)
  //     }
  //     loadingTimeoutRef.current = setTimeout(() => {
  //       hideLoading(FILES_LOADING_CONTAINER)
  //     }, 300)
  //   }
  //   return () => {
  //     if (loadingTimeoutRef.current) {
  //       clearTimeout(loadingTimeoutRef.current)
  //     }
  //   }
  // }, [workspace, showLoading, hideLoading])

  // Guards the one-time auto-open of the X-Ray Files folder when the user
  // arrives from the Charting X-ray panel. Without this, the effect below
  // re-runs whenever `customFolders`/`defaultFolders` repopulate (e.g. after
  // the root workspace re-fetches) and would re-open X-Ray Files, trapping
  // the user there when they click the "Folders" breadcrumb to go to root.
  const chartingXrayFolderAppliedRef = useRef(false)
  useEffect(() => {
    const navState = location.state as FilesLocationState | null
    if (navState?.openFolder) {
      const allFolders = [...defaultFolders, ...customFolders]
      const match = allFolders.find(
        (f) => f.name.toLowerCase() === navState.openFolder!.toLowerCase(),
      )
      if (match) {
        setOpenFolder(match)
      }
      navigate(location.pathname, { replace: true, state: {} })
    }
    // Phase 7 — auto-open DCM Files folder when entering via the Charting
    // X-ray select mode; default-0 is the X-Ray Files default folder.
    // Only apply once so subsequent root-navigation (clicking "Folders")
    // isn't overridden by this effect re-running.
    if (
      navState?.from === 'charting-xray-panel' &&
      navState?.mode === 'select-xray' &&
      !chartingXrayFolderAppliedRef.current
    ) {
      const allFolders = [...defaultFolders, ...customFolders]
      const xray = allFolders.find(
        (f) =>
          f.id === navState.defaultFolderId ||
          f.name.toLowerCase() === 'x-ray files' ||
          f.name.toLowerCase() === 'dcm files',
      )
      if (xray) {
        setOpenFolder(xray)
        chartingXrayFolderAppliedRef.current = true
      }
    }
  }, [
    customFolders,
    defaultFolders,
    location.pathname,
    location.state,
    navigate,
  ])

  // When mounted inside a picker modal (e.g. the X-ray viewer's cloud
  // storage modal), open the requested folder by name as soon as the
  // folder list has loaded. Only auto-applies while no folder is open
  // yet so user navigation isn't overridden mid-session.
  const initialFolderAppliedRef = useRef(false)
  useEffect(() => {
    if (!initialOpenFolderName || initialFolderAppliedRef.current) return
    const allFolders = [...defaultFolders, ...customFolders]
    if (allFolders.length === 0) return
    const target = allFolders.find(
      (f) => f.name.toLowerCase() === initialOpenFolderName.toLowerCase(),
    )
    if (target) {
      setOpenFolder(target)
      initialFolderAppliedRef.current = true
    }
  }, [initialOpenFolderName, defaultFolders, customFolders])

  const loadingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => {
    const activePatientId = selectedPatient?.patient_id
    if (!activePatientId) {
      lastWorkspacePatientIdRef.current = undefined
      setOpenFolder(null)
      return
    }

    const patientChanged =
      lastWorkspacePatientIdRef.current !== undefined &&
      lastWorkspacePatientIdRef.current !== activePatientId

    if (patientChanged) {
      setOpenFolder(null)
    }

    const folderId = patientChanged || !openFolder ? 'root' : openFolder.id

    lastWorkspacePatientIdRef.current = activePatientId

    let cancelled = false
    showLoading({ container: FILES_LOADING_CONTAINER })

    void dispatch(
      fetchWorkspace({
        patient_id: activePatientId,
        folder_id: folderId,
        search: searchTerm || undefined,
        types:
          searchFilters.type && searchFilters.type.length > 0
            ? searchFilters.type.join(',')
            : undefined,
        favorite: showStarred ? 'true' : undefined,
        sort_by:
          sortConfig.sortBy === 'dateModified'
            ? 'date'
            : sortConfig.sortBy === 'name'
              ? 'name'
              : 'type',
        sort_dir: sortConfig.sortDirection === 'zToA' ? 'desc' : 'asc',
        page: 1,
        limit: 100,
      }),
    ).finally(() => {
      if (!cancelled) {
        hideLoading(FILES_LOADING_CONTAINER)
      }
    })

    return () => {
      cancelled = true
      hideLoading(FILES_LOADING_CONTAINER)
    }
  }, [
    dispatch,
    selectedPatient?.patient_id,
    openFolder?.id,
    searchTerm,
    searchFilters.type,
    showStarred,
    sortConfig.sortBy,
    sortConfig.sortDirection,
    openFolder,
    showLoading,
    hideLoading,
  ])

  useEffect(() => {
    if (!workspace) return

    const mappedCustomFolders: FolderItem[] = workspace.folders
      .filter((folder) => !folder.is_default)
      .map((folder) => ({
        id: folder.folder_id,
        name: folder.folder_name,
        isDefault: false,
        isSelected: false,
        parentId:
          folder.parent_folder_id === workspace.scope.root_folder_id
            ? undefined
            : folder.parent_folder_id || undefined,
        path: folder.path,
      }))
    setCustomFolders(mappedCustomFolders)

    const groupedFiles: Record<string, MockFileItem[]> = {}
    workspace.files.forEach((file) => {
      const mapped = mapWorkspaceFileToMock(file)
      const key = file.folder_id || 'root'
      groupedFiles[key] = [...(groupedFiles[key] || []), mapped]
    })
    setFilesByFolder(groupedFiles)
    setSampleFiles(groupedFiles.root || [])
  }, [workspace])

  useEffect(() => {
    setLocalDraftCustomFolders([])
    setLocalDraftMoveModalFolders([])
  }, [selectedPatient?.patient_id])

  useEffect(() => {
    void dispatch(fetchTotalStorage())
  }, [dispatch, selectedPatient?.patient_id])

  useEffect(() => {
    let cancelled = false
    if (!activeClinicId) return
    void getCloudStorageSummary({ clinicId: activeClinicId })
      .then((summary) => {
        if (cancelled) return
        const totalBytes = summary.plan.totalGb * 1024 * 1024 * 1024
        if (Number.isFinite(totalBytes) && totalBytes > 0) {
          setStorageLimitBytes(totalBytes)
        }
      })
      .catch((error) => {
        logger.error('Failed to fetch subscription storage limit:', error)
      })
    return () => {
      cancelled = true
    }
  }, [activeClinicId])

  const patientName = selectedPatient
    ? `${selectedPatient.first_name ?? ''} ${selectedPatient.last_name ?? ''}`.trim()
    : selectionMode
      ? 'Cloud Storage'
      : ''
  const patientRootFolderId = workspace?.scope.root_folder_id || null
  const storageUsedBytes = folderState.totalStorage || 0
  const storagePercent =
    storageLimitBytes > 0
      ? Math.min((storageUsedBytes / storageLimitBytes) * 100, 100)
      : 0

  const refreshWorkspace = useCallback(async (): Promise<void> => {
    const activePatientId = selectedPatient?.patient_id
    if (!activePatientId) return
    showLoading({ container: FILES_LOADING_CONTAINER })
    try {
      await dispatch(
        fetchWorkspace({
          patient_id: activePatientId,
          folder_id: openFolder ? openFolder.id : 'root',
          search: searchTerm || undefined,
          types:
            searchFilters.type && searchFilters.type.length > 0
              ? searchFilters.type.join(',')
              : undefined,
          favorite: showStarred ? 'true' : undefined,
          sort_by:
            sortConfig.sortBy === 'dateModified'
              ? 'date'
              : sortConfig.sortBy === 'name'
                ? 'name'
                : 'type',
          sort_dir: sortConfig.sortDirection === 'zToA' ? 'desc' : 'asc',
          page: 1,
          limit: 100,
        }),
      )
    } finally {
      hideLoading(FILES_LOADING_CONTAINER)
    }
  }, [
    dispatch,
    selectedPatient?.patient_id,
    searchTerm,
    searchFilters.type,
    showStarred,
    sortConfig.sortBy,
    sortConfig.sortDirection,
    openFolder,
    showLoading,
    hideLoading,
  ])

  const filesWorkspaceRefreshKey = folderState.filesWorkspaceRefreshKey
  useEffect(() => {
    if (filesWorkspaceRefreshKey === 0) return
    const id = window.setTimeout(() => {
      void refreshWorkspace()
    }, 100)
    return () => clearTimeout(id)
  }, [filesWorkspaceRefreshKey, refreshWorkspace])

  useEffect(() => {
    if (!attachToClaimsFileId) return
    const pid = selectedPatient?.patient_id
    if (!pid) return
    void dispatch(fetchClaims(pid))
  }, [attachToClaimsFileId, selectedPatient?.patient_id, dispatch])

  const claimsArray = useMemo(() => {
    if (!claimsData) return []
    return Array.isArray(claimsData)
      ? claimsData
      : (((claimsData as Record<string, unknown>).claims as unknown[]) ?? [])
  }, [claimsData])

  const mappedClaimsForModal = useMemo<ClaimRow[]>(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return claimsArray.map((c: any) => {
      const pName = c.patient
        ? `${c.patient.first_name ?? ''} ${c.patient.last_name ?? ''}`.trim()
        : ''
      const carrier = c.insuranceplan?.carrier?.carrier_name ?? c.carrier ?? ''
      const latestStatus = Array.isArray(c.claimstatus)
        ? c.claimstatus[0]
        : c.claimstatus
      const status = latestStatus?.status ?? c.status ?? 'Draft'
      return {
        id: c.claim_id ?? c.id ?? '',
        dateSubmitted: c.claim_date
          ? new Date(c.claim_date).toLocaleDateString()
          : '',
        patientName: pName,
        carrier,
        status,
      }
    })
  }, [claimsArray])

  const mappedProceduresByClaim = useMemo<
    Record<string, ProcedureRow[]>
  >(() => {
    const result: Record<string, ProcedureRow[]> = {}
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    for (const c of claimsArray as any[]) {
      const cId = c.claim_id ?? c.id ?? ''
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const items: any[] = c.claimitems ?? []
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      result[cId] = items.map((item: any) => {
        const procSettings =
          item.procedure_log?.procedure?.procedure_settings ??
          item.treatment_plan_item?.procedure_settings
        const procedureCode =
          item.procedure_log?.procedure?.code ??
          procSettings?.code ??
          item.procedure_log?.procedure?.procedure_name ??
          `Item ${item.claim_item_id?.slice(0, 6) ?? ''}`
        return {
          id: item.claim_item_id ?? '',
          procedureCode,
          procedureDate: item.date_time_created_at
            ? new Date(item.date_time_created_at).toLocaleDateString()
            : '',
          area: '',
          claimAmt: `$${Number(item.amount ?? 0).toFixed(2)}`,
          selectActive: true,
        }
      })
    }
    return result
  }, [claimsArray])

  const mappedProcedureItemsByCode = useMemo<
    Record<string, ProcedureItemRow[]>
  >(() => {
    const result: Record<string, ProcedureItemRow[]> = {}
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    for (const c of claimsArray as any[]) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const items: any[] = c.claimitems ?? []
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      for (const item of items as any[]) {
        const procSettings =
          item.procedure_log?.procedure?.procedure_settings ??
          item.treatment_plan_item?.procedure_settings
        const code =
          item.procedure_log?.procedure?.code ??
          procSettings?.code ??
          item.procedure_log?.procedure?.procedure_name ??
          `Item ${item.claim_item_id?.slice(0, 6) ?? ''}`
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const attachments: any[] = item.claimitemattachments ?? []
        if (attachments.length > 0) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          result[code] = attachments.map((att: any, idx: number) => ({
            id: att.claim_item_attachment_id ?? att.file_id ?? `${code}-${idx}`,
            itemNo: idx + 1,
            formatType:
              att.file?.file_extension?.toUpperCase() ?? att.type ?? '',
            type: att.type || 'Other',
            dateTaken: att.date_time_created_at
              ? new Date(att.date_time_created_at).toLocaleDateString()
              : '',
          }))
        } else {
          result[code] = [
            {
              id: item.claim_item_id ?? `${code}-new`,
              itemNo: 1,
              formatType: '',
              type: 'Other',
              dateTaken: '',
            },
          ]
        }
      }
    }
    return result
  }, [claimsArray])

  const loadMoveModalFolders = useCallback(
    async (scopeFolderId: string): Promise<void> => {
      const activePatientId = selectedPatient?.patient_id
      if (!activePatientId) {
        setMoveModalFolders([])
        setMoveModalFoldersLoading(false)
        return
      }

      setMoveModalFoldersLoading(true)
      showLoading({ container: FILES_LOADING_CONTAINER })
      try {
        const response = await dispatch(
          fetchWorkspaceSnapshot({
            patient_id: activePatientId,
            folder_id: scopeFolderId,
            page: 1,
            limit: 100,
          }),
        )
        if (!fetchWorkspaceSnapshot.fulfilled.match(response)) {
          setMoveModalFolders([])
          return
        }

        const workspaceScope = response.payload
        const rootFolderId = workspaceScope.scope.root_folder_id
        const mapped = workspaceScope.folders.map((folder) => ({
          id: folder.folder_id,
          name: folder.folder_name,
          isDefault: folder.is_default,
          isSelected: false,
          parentId:
            folder.parent_folder_id === rootFolderId
              ? undefined
              : folder.parent_folder_id || undefined,
          path: folder.path,
        }))
        setMoveModalFolders(mapped)
      } finally {
        setMoveModalFoldersLoading(false)
        hideLoading(FILES_LOADING_CONTAINER)
      }
    },
    [selectedPatient?.patient_id, dispatch, showLoading, hideLoading],
  )

  const handleMoveModalBrowseScopeChange = useCallback(
    (scopeFolderId: string) => {
      setMoveModalBrowseScope(scopeFolderId)
      void loadMoveModalFolders(scopeFolderId)
    },
    [loadMoveModalFolders],
  )

  const moveModalDefaultFolders = useMemo<FolderItem[]>(() => {
    if (moveModalFolders === null) return []
    return moveModalFolders.filter((folder) => folder.isDefault)
  }, [moveModalFolders])

  const moveModalCustomFolders = useMemo<FolderItem[]>(() => {
    if (moveModalFolders === null) return []
    const fromApi = moveModalFolders.filter((folder) => !folder.isDefault)
    const draftsHere = localDraftMoveModalFolders.filter((d) => {
      const dbParent = d.draftParentDbFolderId ?? patientRootFolderId
      if (moveModalBrowseScope === ROOT_FOLDER_ID) {
        return dbParent === patientRootFolderId
      }
      return d.parentId === moveModalBrowseScope
    })
    return [...fromApi, ...draftsHere]
  }, [
    moveModalFolders,
    localDraftMoveModalFolders,
    moveModalBrowseScope,
    patientRootFolderId,
  ])

  const moveModalBlockedMoveTargetIds = useMemo(() => {
    if (!moveModal.show) return [] as string[]
    const blocked = new Set<string>()
    if (moveModal.type === 'folder') {
      blocked.add(moveModal.itemId)
    }
    for (const id of moveModal.folderIds ?? []) {
      if (id) blocked.add(id)
    }
    const movingFiles =
      Boolean(moveModal.itemIds?.length) ||
      (moveModal.type === 'file' &&
        Boolean(moveModal.itemId) &&
        !(moveModal.folderIds?.length && !moveModal.itemIds?.length))
    if (movingFiles) {
      if (openFolder?.id) blocked.add(openFolder.id)
      else blocked.add(ROOT_FOLDER_ID)
    }
    return Array.from(blocked)
  }, [
    moveModal.show,
    moveModal.type,
    moveModal.itemId,
    moveModal.itemIds,
    moveModal.folderIds,
    openFolder?.id,
  ])

  const handleCreateFolder = (): void => {
    const activePatientId = selectedPatient?.patient_id
    if (!activePatientId || !patientRootFolderId) return
    const uiParentFolderId = openFolder?.id || null
    const draftParentDbFolderId = uiParentFolderId || patientRootFolderId
    const peerFolders = [
      ...defaultFolders,
      ...customFolders,
      ...localDraftCustomFolders,
    ]
    const folderName = getNextFolderName(
      peerFolders,
      'New Folder',
      uiParentFolderId,
    )
    const folderPath = buildChildFolderPath(
      openFolder?.path,
      activePatientId,
      folderName,
    )
    const draftId = `${LOCAL_DRAFT_FOLDER_ID_PREFIX}${
      globalThis.crypto?.randomUUID?.() ?? String(Date.now())
    }`
    const draft: FolderItem = {
      id: draftId,
      name: folderName,
      isDefault: false,
      isSelected: false,
      parentId: uiParentFolderId ?? undefined,
      path: folderPath,
      isLocalDraft: true,
      draftParentDbFolderId,
    }
    setLocalDraftCustomFolders((prev) => [...prev, draft])
    setRenamingId(draftId)
  }

  /** Create a move-modal local draft folder (persist only after rename confirm), mirroring handleCreateFolder */
  const handleCreateFolderInMoveModal = (
    parentFolderId: string | null,
    parentFolderPath?: string,
  ): void => {
    const activePatientId = selectedPatient?.patient_id
    if (!activePatientId || !patientRootFolderId) return
    const draftParentDbFolderId = parentFolderId || patientRootFolderId
    const scopePeersFromApi = moveModalFolders ?? []
    const scopeDraftPeers = localDraftMoveModalFolders.filter((f) =>
      parentFolderId ? f.parentId === parentFolderId : !f.parentId,
    )
    const peerFolders = [...scopePeersFromApi, ...scopeDraftPeers]
    const folderName = getNextFolderName(
      peerFolders,
      'New Folder',
      parentFolderId,
    )
    const parentFolder = parentFolderId
      ? scopePeersFromApi.find((f) => f.id === parentFolderId) ||
        scopeDraftPeers.find((f) => f.id === parentFolderId) ||
        null
      : null
    const resolvedParentPath = parentFolder?.path ?? parentFolderPath
    const folderPath = buildChildFolderPath(
      resolvedParentPath,
      activePatientId,
      folderName,
    )
    const draftId = `${LOCAL_DRAFT_FOLDER_ID_PREFIX}${
      globalThis.crypto?.randomUUID?.() ?? String(Date.now())
    }`
    const draft: FolderItem = {
      id: draftId,
      name: folderName,
      isDefault: false,
      isSelected: false,
      parentId: parentFolderId ?? undefined,
      path: folderPath,
      isLocalDraft: true,
      draftParentDbFolderId,
      parentPathSnapshot: parentFolderPath,
    }
    setLocalDraftMoveModalFolders((prev) => [...prev, draft])
    setMoveModalRenamingFolderId(draftId)
  }

  const handleUpload = (): void => {
    setShowUploadModal(true)
  }

  const handleUploadAttach = useCallback(
    async (uploadedFiles: FileUpload[]): Promise<void> => {
      const activePatientId = selectedPatient?.patient_id
      if (!activePatientId) return
      const files = uploadedFiles
        .map((f) => f.file)
        .filter((f): f is File => !!f)
      if (files.length === 0) return
      showLoading({ container: FILES_LOADING_CONTAINER })
      try {
        const result = await dispatch(
          uploadFilesAction({
            files,
            patientId: activePatientId,
            folderId: openFolder?.id,
          }),
        )
        if (uploadFilesAction.fulfilled.match(result)) {
          const count = result.payload.length
          showToast(
            createSuccessToast(
              count > 1 ? 'Files uploaded' : 'File uploaded',
              count > 1
                ? `${count} files have been uploaded.`
                : `"${files[0].name}" has been uploaded.`,
            ),
          )
          return
        }
        showToast(
          createFailedToast(
            'Upload failed',
            result.payload || 'Could not upload files.',
          ),
        )
      } finally {
        hideLoading(FILES_LOADING_CONTAINER)
      }
    },
    [
      dispatch,
      hideLoading,
      openFolder?.id,
      selectedPatient?.patient_id,
      showLoading,
      showToast,
    ],
  )

  const handleCustomFolderSelect = (
    folder: FolderItem,
    selected: boolean,
  ): void => {
    setCustomFolders((prev) =>
      prev.map((f) =>
        f.id === folder.id ? { ...f, isSelected: selected } : f,
      ),
    )
    setLocalDraftCustomFolders((prev) =>
      prev.map((f) =>
        f.id === folder.id ? { ...f, isSelected: selected } : f,
      ),
    )
  }

  const handleFolderSelect = (folder: FolderItem): void => {
    if (isLocalDraftFolder(folder)) {
      showToast(
        createFailedToast(
          'New Folder',
          'Enter a name and press Enter or click away to create this folder.',
        ),
      )
      return
    }
    setOpenFolder(folder)
    setSelectedFileIds([])
  }

  const handleBreadcrumbRootClick = (): void => {
    setOpenFolder(null)
    setSelectedFileIds([])
  }

  const handleFileSelect = (fileId: string): void => {
    setSelectedFileIds((prev) =>
      prev.includes(fileId)
        ? prev.filter((id) => id !== fileId)
        : [...prev, fileId],
    )
  }

  const handleFileFavorite = async (fileId: string): Promise<void> => {
    const file = allVisibleFiles.find((f) => f.id === fileId)
    const isNowFavorite = !(file?.isFavorite ?? false)
    const result = await dispatch(
      toggleFileFavoriteAction({ fileId, isFavorite: isNowFavorite }),
    )
    if (toggleFileFavoriteAction.fulfilled.match(result)) {
      showToast(
        createSuccessToast(
          isNowFavorite ? 'Added to favorites' : 'Removed from favorites',
          isNowFavorite
            ? `"${file?.fileName ?? ''}" has been added to favorites.`
            : `"${file?.fileName ?? ''}" has been removed from favorites.`,
        ),
      )
      return
    }
    showToast(
      createFailedToast(
        'Favorite update failed',
        'Could not update file favorite status.',
      ),
    )
  }

  const handleFileDelete = (fileId: string): void => {
    const idsToRemove =
      selectedFileIds.includes(fileId) && selectedFileIds.length > 1
        ? selectedFileIds
        : [fileId]
    const file = allVisibleFiles.find((f) => f.id === idsToRemove[0])
    const name =
      idsToRemove.length > 1
        ? `${idsToRemove.length} files`
        : (file?.fileName ?? '')
    setDeleteConfirm({
      show: true,
      type: 'file',
      ids: idsToRemove,
      name,
    })
  }

  const confirmFileDelete = useCallback(async (): Promise<void> => {
    const { ids } = deleteConfirm
    const idSet = new Set(ids)

    // Snapshot DICOM identifiers for every file being deleted BEFORE the
    // delete runs, since the workspace will be refetched/cleared once the
    // backend ack arrives. We use this to clean up the Azure DICOMweb store
    // as a defense-in-depth measure: the business-layer delete endpoint
    // already attempts this (see folder-module deleteFile), but if any of
    // those Azure calls silently fail or are skipped, the DICOM study would
    // otherwise stick around forever.
    const allKnownFiles: MockFileItem[] = [
      ...allVisibleFiles,
      ...Object.values(filesByFolder).flat(),
      ...sampleFiles,
    ]
    const dicomTargetsToDelete: Array<{
      fileId: string
      studyUid: string
      seriesUid: string
    }> = []
    for (const id of ids) {
      const file = allKnownFiles.find((f) => f.id === id)
      const studyUid = (file?.studyUid ?? file?.study_uid ?? '').trim()
      const seriesUid = (file?.seriesUid ?? file?.series_uid ?? '').trim()
      if (studyUid && seriesUid) {
        dicomTargetsToDelete.push({ fileId: id, studyUid, seriesUid })
      }
    }

    let failures = 0
    const failureMessages: string[] = []
    const deletedIds: string[] = []
    for (const id of ids) {
      const result = await dispatch(deleteFileAction(id))
      if (deleteFileAction.rejected.match(result)) {
        failures += 1
        const message = String(result.payload || '').trim()
        if (message) {
          failureMessages.push(message)
        }
      } else {
        deletedIds.push(id)
      }
    }

    // Defense-in-depth: for each successfully-deleted DICOM file, attempt to
    // remove the corresponding series from Azure DICOMweb when no remaining
    // file in the patient workspace references the same series. We
    // intentionally swallow errors here — the DB row is already gone and
    // the backend may have already cleaned Azure up; this is a safety net,
    // not a hard failure path.
    if (dicomTargetsToDelete.length > 0) {
      const remainingFiles = allKnownFiles.filter(
        (f) => !idSet.has(f.id) && !deletedIds.includes(f.id),
      )
      const remainingSeriesUids = new Set(
        remainingFiles
          .map((f) => (f.seriesUid ?? f.series_uid ?? '').trim())
          .filter(Boolean),
      )
      for (const target of dicomTargetsToDelete) {
        if (!deletedIds.includes(target.fileId)) continue
        if (remainingSeriesUids.has(target.seriesUid)) continue
        try {
          await deleteOrphanDicomStudy(target.studyUid, target.seriesUid)
        } catch (err) {
          console.warn('[Files] DICOMweb cleanup failed for', target, err)
        }
      }
    }

    setSelectedFileIds((prev) => prev.filter((id) => !ids.includes(id)))
    if (previewFile && ids.includes(previewFile.id)) {
      setPreviewFile(null)
      setIsPreviewFullscreen(false)
    }

    const count = ids.length
    if (failures === 0) {
      showToast(
        createSuccessToast(
          count > 1 ? 'Files removed' : 'File removed',
          count > 1
            ? `${count} files have been removed.`
            : 'The file has been removed.',
        ),
      )
    } else {
      const detail =
        failureMessages[0] || `${failures} file(s) could not be removed.`
      showToast(createFailedToast('Delete failed', detail))
    }
    setDeleteConfirm({ show: false, type: 'file', ids: [], name: '' })
  }, [
    deleteConfirm,
    previewFile,
    showToast,
    dispatch,
    allVisibleFiles,
    filesByFolder,
    sampleFiles,
  ])

  const triggerDownload = useCallback((blob: Blob, fileName: string): void => {
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = fileName
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }, [])

  const handleFileDownload = useCallback(
    async (fileId: string): Promise<void> => {
      const file =
        allVisibleFiles.find((f) => f.id === fileId) ??
        Object.values(filesByFolder)
          .flat()
          .find((f) => f.id === fileId) ??
        sampleFiles.find((f) => f.id === fileId)
      if (!file) return
      const fileName = file.fileName
      try {
        const response = await dispatch(fetchFileDownload(fileId))
        if (fetchFileDownload.fulfilled.match(response)) {
          const payload = response.payload
          if ('viewer_url' in payload && payload.viewer_url) {
            window.open(payload.viewer_url, '_blank', 'noopener,noreferrer')
            showToast(createSuccessToast('Opened in DICOM viewer', fileName))
            return
          }
          if ('url' in payload && payload.url) {
            try {
              const fileResponse = await fetch(payload.url)
              if (!fileResponse.ok) {
                throw new Error(
                  `Download failed with status ${fileResponse.status}`,
                )
              }
              const fileBlob = await fileResponse.blob()
              triggerDownload(fileBlob, fileName)
            } catch {
              // Fallback when cross-origin fetch is blocked by CORS.
              window.open(payload.url, '_blank', 'noopener,noreferrer')
            }
            showToast(createSuccessToast('Download started', fileName))
            return
          }
        }
      } catch {
        // fall through
      }
      const placeholder = new Blob(
        [`Placeholder for ${fileName} (mock file)`],
        { type: 'text/plain' },
      )
      triggerDownload(placeholder, fileName)
      showToast(createSuccessToast('Download started', fileName))
    },
    [
      allVisibleFiles,
      filesByFolder,
      sampleFiles,
      triggerDownload,
      showToast,
      dispatch,
    ],
  )

  const handleFileViewFile = async (fileId: string): Promise<void> => {
    const file = allVisibleFiles.find((f) => f.id === fileId)
    if (!file) return
    setPreviewFile(file)
    setIsPreviewOpen(true)
    setIsPreviewFullscreen(false)
  }

  const fetchFileFromUrl = useCallback(
    async (url: string, fileName: string): Promise<File | null> => {
      try {
        const response = await fetch(url)
        if (!response.ok) {
          throw new Error(`Failed to fetch file: ${response.statusText}`)
        }
        const blob = await response.blob()
        const ext = fileName.toLowerCase().substring(fileName.lastIndexOf('.'))
        const mimeType =
          ext === '.dcm' || ext === '.dicom'
            ? 'application/dicom'
            : blob.type || 'application/octet-stream'
        return new File([blob], fileName, { type: mimeType })
      } catch (err: any) {
        const msg =
          typeof err === 'string' ? err : err?.message || 'Download failed'
        showToast(
          createFailedToast(
            'File Download Failed',
            `Could not download "${fileName}": ${msg}`,
          ),
        )
        return null
      }
    },
    [showToast],
  )

  const uploadImageForDicom = useCallback(
    async (
      file: MockFileItem,
    ): Promise<{ studyUid: string; seriesUid: string } | null> => {
      if (file.studyUid?.trim() && file.seriesUid?.trim()) {
        return {
          studyUid: file.studyUid.trim(),
          seriesUid: file.seriesUid.trim(),
        }
      }

      if (!selectedPatient?.patient_id) return null

      // ---------------------------------------------------------------
      // Idempotency: if this JPG/PNG was already converted previously we
      // must re-use the existing DICOM instead of producing another copy.
      // The backend persists the source file name in `original_name` of
      // the resulting .dcm row, which is the only stable join key.
      // ---------------------------------------------------------------
      const propagateExistingUids = (
        studyUid: string,
        seriesUid: string,
        sopUid?: string,
      ): { studyUid: string; seriesUid: string } => {
        const updateFile = (f: MockFileItem): MockFileItem =>
          f.id === file.id
            ? {
                ...f,
                studyUid,
                seriesUid,
                sopUid: sopUid ?? f.sopUid,
              }
            : f
        setSampleFiles((prev) => prev.map(updateFile))
        setFilesByFolder((prev) => {
          const updated = { ...prev }
          for (const fKey of Object.keys(updated)) {
            updated[fKey] = updated[fKey].map(updateFile)
          }
          return updated
        })
        return { studyUid, seriesUid }
      }

      const sourceName = file.originalName || file.fileName
      const isMatchingDicom = (candidate: {
        id?: string
        isDicom?: boolean
        studyUid?: string
        seriesUid?: string
        sopUid?: string
        originalName?: string
        fileName?: string
      }): boolean => {
        if (candidate.id === file.id) return false
        if (!candidate.studyUid || !candidate.seriesUid) return false
        if (candidate.isDicom === false) return false
        const candidateOriginal = candidate.originalName || candidate.fileName
        return candidateOriginal === sourceName
      }

      // 1) Cheap in-memory scan over everything the page currently knows
      //    about (workspace files for the open folder + cached siblings).
      const knownFiles: MockFileItem[] = [
        ...sampleFiles,
        ...Object.values(filesByFolder).flat(),
      ]
      const inMemoryMatch = knownFiles.find(isMatchingDicom)
      if (inMemoryMatch?.studyUid && inMemoryMatch.seriesUid) {
        return propagateExistingUids(
          inMemoryMatch.studyUid,
          inMemoryMatch.seriesUid,
          inMemoryMatch.sopUid,
        )
      }

      // 2) Patient folder hierarchy covers files in folders the user has
      //    not opened yet (e.g. JPG sits at root but its .dcm is inside
      //    "X-Ray Files" from a prior session).
      try {
        const folderResult = await dispatch(
          fetchPatientFolder(selectedPatient.patient_id),
        )
        const folder = folderResult.payload as IFolder | undefined
        if (folder) {
          const flatFiles: IFile[] = [
            ...(folder.files || []),
            ...(folder.child_folders || []).flatMap((cf) => cf.files || []),
          ]
          const persistedMatch = flatFiles.find(
            (f) =>
              f.file_id !== file.id &&
              f.is_dicom &&
              f.study_uid &&
              f.series_uid &&
              (f.original_name === sourceName || f.file_name === sourceName),
          )
          if (persistedMatch?.study_uid && persistedMatch.series_uid) {
            return propagateExistingUids(
              persistedMatch.study_uid,
              persistedMatch.series_uid,
              persistedMatch.sop_uid ?? undefined,
            )
          }
        }
      } catch {
        // Non-fatal — fall through to the upload path below.
      }

      let fileToUpload = file.rawFile ?? null

      if (!fileToUpload && file.previewUrl) {
        fileToUpload = await fetchFileFromUrl(file.previewUrl, file.fileName)
      }

      // Real-DB files mapped from the workspace API have neither `rawFile`
      // nor `previewUrl` populated — they only carry an `id`. Resolve a
      // signed download URL via the backend, then materialise the blob so
      // we can re-upload it to convert/register it in the DICOM web store.
      if (!fileToUpload) {
        try {
          const downloadResponse = await dispatch(fetchFileDownload(file.id))
          if (fetchFileDownload.fulfilled.match(downloadResponse)) {
            const payload = downloadResponse.payload
            // Backend may already have it as DICOM — short-circuit with
            // the returned UIDs (matches the previous mockup behaviour
            // where uploadImageForDicom returned UIDs without uploading).
            if (
              'is_dicom' in payload &&
              payload.is_dicom &&
              payload.study_uid &&
              payload.series_uid
            ) {
              return {
                studyUid: payload.study_uid,
                seriesUid: payload.series_uid,
              }
            }
            if ('url' in payload && payload.url) {
              fileToUpload = await fetchFileFromUrl(payload.url, file.fileName)
            }
          }
        } catch {
          // fall through; null check below will surface failure
        }
      }

      if (!fileToUpload) return null

      // Place the converted .dcm in the same folder as the source image
      // so it appears alongside it (e.g. inside "X-Ray Files" when the
      // JPG/PNG lives there). Fall back to the patient's X-Ray Files
      // folder when the source has no known folder context.
      const targetFolderId =
        file.folderId !== undefined && file.folderId !== null
          ? file.folderId
          : xrayFolderId

      try {
        const result = await dispatch(
          uploadFileAction({
            file: fileToUpload,
            patientId: selectedPatient.patient_id,
            folderId: targetFolderId,
          }),
        ).unwrap()

        if (result?.study_uid && result?.series_uid) {
          const updateFile = (f: MockFileItem): MockFileItem =>
            f.id === file.id
              ? {
                  ...f,
                  studyUid: result.study_uid!,
                  seriesUid: result.series_uid!,
                  sopUid: result.sop_uid ?? undefined,
                }
              : f
          setSampleFiles((prev) => prev.map(updateFile))
          setFilesByFolder((prev) => {
            const updated = { ...prev }
            for (const folderId of Object.keys(updated)) {
              updated[folderId] = updated[folderId].map(updateFile)
            }
            return updated
          })
          return {
            studyUid: result.study_uid!,
            seriesUid: result.series_uid!,
          }
        }
      } catch (err: any) {
        const msg =
          typeof err === 'string' ? err : err?.message || 'Upload failed'
        const isAlreadyExists =
          msg.toLowerCase().includes('already exists') ||
          msg.toLowerCase().includes('duplicate')

        if (isAlreadyExists) {
          const folderResult = await dispatch(
            fetchPatientFolder(selectedPatient.patient_id),
          )
          const folder = folderResult.payload as IFolder | undefined
          if (folder) {
            const allFiles = [
              ...(folder.files || []),
              ...(folder.child_folders || []).flatMap((cf) => cf.files || []),
            ]
            const existing = allFiles.find(
              (f) =>
                f.is_dicom &&
                f.study_uid &&
                f.series_uid &&
                (f.file_name === file.fileName ||
                  f.original_name === file.fileName),
            )
            if (existing?.study_uid && existing?.series_uid) {
              const updateFile = (f: MockFileItem): MockFileItem =>
                f.id === file.id
                  ? {
                      ...f,
                      studyUid: existing.study_uid!,
                      seriesUid: existing.series_uid!,
                    }
                  : f
              setSampleFiles((prev) => prev.map(updateFile))
              setFilesByFolder((prev) => {
                const updated = { ...prev }
                for (const fKey of Object.keys(updated)) {
                  updated[fKey] = updated[fKey].map(updateFile)
                }
                return updated
              })
              return {
                studyUid: existing.study_uid!,
                seriesUid: existing.series_uid!,
              }
            }
          }
        }

        if (!isAlreadyExists) {
          showToast(createFailedToast('DICOM Conversion Failed', msg))
        }
      }
      return null
    },
    [
      dispatch,
      selectedPatient?.patient_id,
      xrayFolderId,
      showToast,
      fetchFileFromUrl,
      sampleFiles,
      filesByFolder,
    ],
  )

  // Expose the resolver to the forward-declared ref used by
  // `handleOpenSelection` (which is rendered higher up in the function
  // body — see comment near the ref declaration for context).
  uploadImageForDicomRef.current = uploadImageForDicom

  const handleFileViewWithDicom = useCallback(
    async (fileId: string): Promise<void> => {
      const file = allVisibleFiles.find((f) => f.id === fileId)
      if (!file) return

      if (file.studyUid?.trim() && file.seriesUid?.trim()) {
        navigate('/image-xrayviewer', {
          state: {
            dicomFiles: [
              {
                id: file.id,
                fileName: file.fileName,
                originalName: file.fileName,
                studyUid: file.studyUid.trim(),
                seriesUid: file.seriesUid.trim(),
                sopUid: file.sopUid?.trim() || undefined,
              },
            ],
          },
        })
        return
      }

      // No DICOM UIDs yet — auto-upload/convert. This covers three cases:
      //   1. In-memory file (rawFile) — fresh upload from the picker
      //   2. Sample/mock file with a preview URL
      //   3. Real DB file with neither — uploadImageForDicom will resolve
      //      a download URL via fetchFileDownload and re-upload the blob
      showLoading({ container: FILES_LOADING_CONTAINER })
      const uids = await uploadImageForDicom(file)
      hideLoading(FILES_LOADING_CONTAINER)
      if (uids) {
        await dispatch(fetchPatientFolder(selectedPatient!.patient_id))
        navigate('/image-xrayviewer', {
          state: {
            dicomFiles: [
              {
                id: file.id,
                fileName: file.fileName,
                originalName: file.fileName,
                studyUid: uids.studyUid,
                seriesUid: uids.seriesUid,
              },
            ],
          },
        })
        return
      }

      showToast(
        createFailedToast(
          'DICOM Upload Failed',
          'Could not process the file. Please try uploading directly on the X-Ray Viewer page.',
        ),
      )
    },
    [
      allVisibleFiles,
      uploadImageForDicom,
      navigate,
      dispatch,
      selectedPatient,
      showLoading,
      hideLoading,
      showToast,
    ],
  )

  const handleFileViewWithDicomMultiple = useCallback(
    async (fileIds: string[]): Promise<void> => {
      const resolveFile = (id: string): MockFileItem | undefined =>
        allVisibleFiles.find((f) => f.id === id) ??
        Object.values(filesByFolder)
          .flat()
          .find((f) => f.id === id) ??
        sampleFiles.find((f) => f.id === id)

      const files = fileIds
        .map(resolveFile)
        .filter((f): f is MockFileItem => !!f)
        .filter((f) => {
          const kind = getFileTypeFromFileName(f.fileName)
          return kind === 'image' || kind === 'dcm'
        })

      if (files.length === 0) return

      // Any file missing DICOM UIDs needs to be uploaded/converted first.
      // We no longer gate on rawFile/previewUrl — uploadImageForDicom will
      // resolve a download URL via fetchFileDownload for real-DB files.
      const filesToUpload = files.filter((f) => !f.studyUid)
      const uploadedUidsById = new Map<
        string,
        { studyUid: string; seriesUid: string }
      >()

      if (filesToUpload.length > 0) {
        showLoading({ container: FILES_LOADING_CONTAINER })
        for (const img of filesToUpload) {
          const uids = await uploadImageForDicom(img)
          if (uids) uploadedUidsById.set(img.id, uids)
        }
        await dispatch(fetchPatientFolder(selectedPatient!.patient_id))
        hideLoading(FILES_LOADING_CONTAINER)
      }

      const dicomFiles = files.map((f) => {
        const fresh = uploadedUidsById.get(f.id)
        return {
          id: f.id,
          fileName: f.fileName,
          originalName: f.fileName,
          studyUid: fresh?.studyUid ?? f.studyUid,
          seriesUid: fresh?.seriesUid ?? f.seriesUid,
          sopUid: f.sopUid,
        }
      })

      navigate('/image-xrayviewer', { state: { dicomFiles } })
    },
    [
      allVisibleFiles,
      filesByFolder,
      sampleFiles,
      uploadImageForDicom,
      navigate,
      dispatch,
      selectedPatient,
      showLoading,
      hideLoading,
    ],
  )

  const showDicomInSelectionBar = useMemo(() => {
    const resolveFile = (id: string): MockFileItem | undefined =>
      allVisibleFiles.find((f) => f.id === id) ??
      Object.values(filesByFolder)
        .flat()
        .find((f) => f.id === id) ??
      sampleFiles.find((f) => f.id === id)
    return selectedFileIds.some((id) => {
      const f = resolveFile(id)
      if (!f) return false
      const kind = getFileTypeFromFileName(f.fileName)
      return kind === 'image' || kind === 'dcm'
    })
  }, [selectedFileIds, allVisibleFiles, filesByFolder, sampleFiles])

  const handleFileNotes = async (fileId: string): Promise<void> => {
    if (!selectedPatient?.patient_id) {
      showToast(createFailedToast('Add note', 'Please select a patient first'))
      return
    }
    const file =
      allVisibleFiles.find((f) => f.id === fileId) ??
      Object.values(filesByFolder)
        .flat()
        .find((f) => f.id === fileId) ??
      sampleFiles.find((f) => f.id === fileId)
    if (!file) {
      showToast(createFailedToast('Notes', 'Could not find this file.'))
      return
    }
    const selectedPatientId = selectedPatient.patient_id
    let notesList: INote[] = Array.isArray(notesData) ? notesData : []
    if (notesData === null) {
      const notesResult = await dispatch(
        fetchAllNotes({ patientId: selectedPatientId }),
      )
      if (fetchAllNotes.fulfilled.match(notesResult)) {
        notesList = notesResult.payload
      }
    }
    const openFirstNote = patientHasFileContextNotes(
      notesList,
      selectedPatientId,
      file.id,
    )
    openNoteEditor({
      patient: selectedPatient,
      contextId: file.id,
      contextType: NoteContextType.FILES,
      contextName: file.fileName,
      pagePreviousState: { path: location.pathname || '/files' },
      openFirstNote,
      initialView: openFirstNote ? 'list' : 'add',
      editorBackButtonLabel: filesNotesBackLabel,
      openContextNotesByDefault: true,
      overlayBackLabel: filesNotesBackLabel,
      notesFetchLoadingContainer: 'body',
      portalTarget: FILES_LOADING_CONTAINER,
      anchorClassName: 'files-note-editor-overlay-anchor',
      hideNotesTable: true,
    })
  }

  const handleFileDetails = async (fileId: string): Promise<void> => {
    await dispatch(fetchFileDetails(fileId))
    const file = allVisibleFiles.find((f) => f.id === fileId) ?? null
    setDetailsFile(file)
  }

  const handleFileMakeCopy = async (fileId: string): Promise<void> => {
    const file = allVisibleFiles.find((f) => f.id === fileId)
    if (!file) return
    const result = await dispatch(copyFileAction({ fileId }))
    if (copyFileAction.fulfilled.match(result)) {
      showToast(
        createSuccessToast('Copy created', `"${file.fileName}" copied.`),
      )
      return
    }
    showToast(
      createFailedToast(
        'Copy unavailable',
        result.payload || 'File copy endpoint is not available yet.',
      ),
    )
  }

  const handleFileRename = (fileId: string): void => {
    setRenamingId(fileId)
  }

  const handleFileRenameConfirm = async (
    fileId: string,
    newName: string,
  ): Promise<void> => {
    const trimmed = newName.trim()
    if (!trimmed) {
      setRenamingId(null)
      return
    }

    const original = allVisibleFiles.find((f) => f.id === fileId)
    const oldName = original?.fileName ?? ''

    const result = await dispatch(
      renameFileAction({
        fileId,
        fileName: trimmed,
      }),
    )
    setRenamingId(null)
    if (renameFileAction.fulfilled.match(result)) {
      if (trimmed !== oldName) {
        showToast(createRenamedSuccessfullyToast(oldName, trimmed))
      }
      return
    }
    showToast(
      createFailedToast(
        'Rename failed',
        result.payload || 'Could not rename file.',
      ),
    )
  }

  const handleFileMove = (fileId: string): void => {
    const file = allVisibleFiles.find((f) => f.id === fileId)
    if (file) {
      setMoveModal({
        show: true,
        itemNames: [file.fileName],
        type: 'file',
        itemId: fileId,
      })
    }
  }

  const handleFileMoveMultiple = (fileIds: string[]): void => {
    if (fileIds.length === 0) return
    const names = fileIds.map(
      (id) =>
        allVisibleFiles.find((f) => f.id === id)?.fileName ??
        Object.values(filesByFolder)
          .flat()
          .find((f) => f.id === id)?.fileName ??
        sampleFiles.find((f) => f.id === id)?.fileName ??
        'File',
    )
    setMoveModal({
      show: true,
      itemNames: names,
      type: 'file',
      itemId: fileIds[0],
      itemIds: fileIds,
    })
  }

  const handleMoveFilesAndFolders = (
    fileIds: string[],
    folderIds: string[],
  ): void => {
    if (fileIds.length === 0 && folderIds.length === 0) return
    const fileNames = fileIds.map(
      (id) =>
        allVisibleFiles.find((f) => f.id === id)?.fileName ??
        Object.values(filesByFolder)
          .flat()
          .find((f) => f.id === id)?.fileName ??
        sampleFiles.find((f) => f.id === id)?.fileName ??
        'File',
    )
    const folderNames = folderIds.map(
      (id) => mergedCustomFolders.find((f) => f.id === id)?.name ?? 'Folder',
    )
    setMoveModal({
      show: true,
      itemNames: [...fileNames, ...folderNames],
      type: 'file',
      itemId: fileIds[0] ?? folderIds[0] ?? '',
      itemIds: fileIds.length > 0 ? fileIds : undefined,
      folderIds: folderIds.length > 0 ? folderIds : undefined,
    })
  }

  const handleFileAttachToClaims = (fileId: string): void => {
    if (!CLAIMS_ATTACHMENT_ENABLED) {
      showToast(
        createFailedToast(
          'Coming soon',
          'Attach to claims will be enabled when backend endpoint is finalized.',
        ),
      )
      return
    }
    setAttachToClaimsFileId(fileId)
  }

  const handleAttachToClaimsHide = (): void => {
    setAttachToClaimsFileId(null)
  }

  const handleAttachToClaim = async (
    claimId: string,
    claim: ClaimRow,
    type: string,
  ): Promise<void> => {
    if (!CLAIMS_ATTACHMENT_ENABLED || !attachToClaimsFileId) return
    const fileName =
      allVisibleFiles.find((f) => f.id === attachToClaimsFileId)?.fileName ??
      'File'
    const result = await dispatch(
      attachFileToClaimAction({
        fileId: attachToClaimsFileId,
        claimId,
        type,
      }),
    )
    if (attachFileToClaimAction.fulfilled.match(result)) {
      showToast(
        createFileAttachedToClaimToast(fileName, claim.patientName, () =>
          navigate('/claims', {
            state: { viewClaim: true, claimId },
          }),
        ),
      )
    } else {
      showToast(
        createFailedToast(
          'Attachment failed',
          result.payload || 'Could not attach file to claim.',
        ),
      )
    }
    handleAttachToClaimsHide()
  }

  const handleAttachToProcedure = (
    _claimId: string,
    _claim: ClaimRow,
  ): void => {
    // Modal drills into procedures table; no dispatch needed here
  }

  const handleAttachToProcedureItem = async (
    claimId: string,
    claimItemId: string,
    _procedureCode: string,
    _itemNo: number,
    claimPatientName: string,
    type: string,
  ): Promise<void> => {
    if (!CLAIMS_ATTACHMENT_ENABLED || !attachToClaimsFileId) return
    const fileName =
      allVisibleFiles.find((f) => f.id === attachToClaimsFileId)?.fileName ??
      'File'

    const result = await dispatch(
      attachFileToClaimItemAction({
        fileId: attachToClaimsFileId,
        claimItemId,
        type,
      }),
    )
    if (attachFileToClaimItemAction.fulfilled.match(result)) {
      showToast(
        createFileAttachedToClaimToast(fileName, claimPatientName, () =>
          navigate('/claims', {
            state: { viewClaim: true, claimId },
          }),
        ),
      )
    } else {
      showToast(
        createFailedToast(
          'Attachment failed',
          result.payload || 'Could not attach file to claim item.',
        ),
      )
    }
    handleAttachToClaimsHide()
  }

  const handleFolderDownloadZip = useCallback(
    async (folder: FolderItem): Promise<void> => {
      if (isLocalDraftFolder(folder)) {
        showToast(
          createFailedToast(
            'Download folder',
            'Create the folder with a name before downloading as ZIP.',
          ),
        )
        return
      }
      const estimateResult = await dispatch(fetchFolderZipEstimate(folder.id))
      if (
        fetchFolderZipEstimate.fulfilled.match(estimateResult) &&
        !estimateResult.payload.can_download
      ) {
        showToast(
          createFailedToast(
            'Download folder',
            estimateResult.payload.reasons.join(' ') ||
              'Folder cannot be downloaded as ZIP.',
          ),
        )
        return
      }

      const downloadResult = await dispatch(triggerFolderZipDownload(folder.id))
      if (triggerFolderZipDownload.fulfilled.match(downloadResult)) {
        const safeName = folder.name.replace(/[/\\?%*:|"<>]/g, '-') + '.zip'
        downloadBlobFile(downloadResult.payload.blob, safeName)
        showToast(createSuccessToast('Download started', `${folder.name}.zip`))
        return
      }
      showToast(
        createFailedToast('Download folder', 'Failed to generate folder ZIP.'),
      )
    },
    [dispatch, showToast],
  )

  const handleFolderDetails = async (folder: FolderItem): Promise<void> => {
    if (isLocalDraftFolder(folder)) {
      showToast(
        createFailedToast(
          'Folder details',
          'Create the folder with a name before opening details.',
        ),
      )
      return
    }
    await dispatch(fetchFolderDetails(folder.id))
    setDetailsFolder(folder)
  }

  const getUniqueCopyFolderName = useCallback(
    (baseName: string): string => {
      const allFolderNames = new Set([
        ...defaultFolders.map((f) => f.name),
        ...mergedCustomFolders.map((f) => f.name),
      ])
      let copyName = `${baseName} (Copy)`
      let counter = 2
      while (allFolderNames.has(copyName)) {
        copyName = `${baseName} (Copy ${counter})`
        counter++
      }
      return copyName
    },
    [mergedCustomFolders, defaultFolders],
  )

  const handleFolderMakeCopy = async (folder: FolderItem): Promise<void> => {
    if (folder.isDefault) {
      showToast(
        createFailedToast(
          'Default Folders',
          'Default patient folders cannot be renamed, copied, or moved.',
        ),
      )
      return
    }
    if (isLocalDraftFolder(folder)) {
      showToast(
        createFailedToast(
          'Copy folder',
          'Create the folder with a name before making a copy.',
        ),
      )
      return
    }

    const copyFolderName = getUniqueCopyFolderName(folder.name)
    const result = await dispatch(
      copyFolderAction({
        folderId: folder.id,
        newName: copyFolderName,
      }),
    )
    if (copyFolderAction.fulfilled.match(result)) {
      showToast(createFoldersCopiedToast())
      return
    }
    showToast(
      createFailedToast(
        'Copy folder failed',
        result.payload || 'Could not copy folder.',
      ),
    )
  }

  const handleFolderRename = (folder: FolderItem): void => {
    if (folder.isDefault) {
      showToast(
        createFailedToast(
          'Default Folders',
          'Default patient folders cannot be renamed, copied, or moved.',
        ),
      )
      return
    }
    setRenamingId(folder.id)
  }

  const handleFolderRenameConfirm = async (
    folderId: string,
    newName: string,
  ): Promise<void> => {
    const trimmed = newName.trim()
    const fromWorkspaceDrafts = localDraftCustomFolders.find(
      (f) => f.id === folderId,
    )
    const fromMoveModalDrafts = localDraftMoveModalFolders.find(
      (f) => f.id === folderId,
    )
    const draftFolder = fromWorkspaceDrafts || fromMoveModalDrafts

    if (draftFolder) {
      if (!trimmed) {
        if (fromWorkspaceDrafts) {
          setLocalDraftCustomFolders((p) => p.filter((f) => f.id !== folderId))
        }
        if (fromMoveModalDrafts) {
          setLocalDraftMoveModalFolders((p) =>
            p.filter((f) => f.id !== folderId),
          )
        }
        setRenamingId(null)
        setMoveModalRenamingFolderId(null)
        return
      }
      const activePatientId = selectedPatient?.patient_id
      if (!activePatientId || !patientRootFolderId) {
        setRenamingId(null)
        setMoveModalRenamingFolderId(null)
        return
      }
      const parentFolderForPath =
        draftFolder.parentId == null
          ? undefined
          : ([
              ...defaultFolders,
              ...customFolders,
              ...localDraftCustomFolders,
              ...(moveModalFolders ?? []),
              ...localDraftMoveModalFolders,
            ].find((f) => f.id === draftFolder.parentId)?.path ??
            draftFolder.parentPathSnapshot)
      const folderPath = buildChildFolderPath(
        parentFolderForPath,
        activePatientId,
        trimmed,
      )
      const parent_folder_id =
        draftFolder.draftParentDbFolderId ?? patientRootFolderId
      const result = await dispatch(
        createNewFolder({
          patient_id: activePatientId,
          folder_name: trimmed,
          path: folderPath,
          parent_folder_id: parent_folder_id,
        }),
      )
      setRenamingId(null)
      if (createNewFolder.fulfilled.match(result)) {
        if (fromWorkspaceDrafts) {
          setLocalDraftCustomFolders((p) => p.filter((f) => f.id !== folderId))
        }
        if (fromMoveModalDrafts) {
          setLocalDraftMoveModalFolders((p) =>
            p.filter((f) => f.id !== folderId),
          )
        }
        showToast(
          createSuccessToast('New Folder', `"${trimmed}" has been created.`),
        )
        return
      }
      if (fromMoveModalDrafts) {
        setMoveModalRenamingFolderId(folderId)
      } else {
        setRenamingId(folderId)
      }
      showToast(
        createFailedToast(
          'New Folder',
          result.payload || 'Could not create folder.',
        ),
      )
      return
    }

    if (!trimmed) {
      setRenamingId(null)
      return
    }
    const original =
      mergedCustomFolders.find((f) => f.id === folderId) ||
      defaultFolders.find((f) => f.id === folderId)
    const oldName = original?.name ?? ''

    const result = await dispatch(
      renameFolderAction({
        folderId,
        newName: trimmed,
      }),
    )
    setRenamingId(null)
    if (renameFolderAction.fulfilled.match(result)) {
      if (trimmed !== oldName) {
        showToast(createRenamedSuccessfullyToast(oldName, trimmed))
      }
      return
    }
    showToast(
      createFailedToast(
        'Rename failed',
        result.payload || 'Could not rename folder.',
      ),
    )
  }

  const handleFolderRenameEscape = (folderId: string): void => {
    if (isLocalDraftFolderId(folderId)) {
      setLocalDraftCustomFolders((p) => p.filter((f) => f.id !== folderId))
      setLocalDraftMoveModalFolders((p) => p.filter((f) => f.id !== folderId))
      setRenamingId(null)
      setMoveModalRenamingFolderId(null)
      return
    }
    const folder =
      mergedCustomFolders.find((f) => f.id === folderId) ||
      defaultFolders.find((f) => f.id === folderId)
    if (folder) void handleFolderRenameConfirm(folderId, folder.name)
  }

  const handleMoveModalFolderRenameConfirm = async (
    folderId: string,
    newName: string,
  ): Promise<void> => {
    showLoading({ container: FILES_LOADING_CONTAINER })
    try {
      await handleFolderRenameConfirm(folderId, newName)
    } finally {
      hideLoading(FILES_LOADING_CONTAINER)
    }
    setMoveModalRenamingFolderId(null)
    if (moveModal.show) {
      await loadMoveModalFolders(moveModalBrowseScope)
    }
  }

  const handleFolderMove = (folder: FolderItem): void => {
    if (folder.isDefault) {
      showToast(
        createFailedToast(
          'Default Folders',
          'Default patient folders cannot be renamed, copied, or moved.',
        ),
      )
      return
    }
    if (isLocalDraftFolder(folder)) {
      showToast(
        createFailedToast(
          'Move folder',
          'Create the folder with a name before moving it.',
        ),
      )
      return
    }
    setMoveModal({
      show: true,
      itemNames: [folder.name],
      type: 'folder',
      itemId: folder.id,
    })
  }

  const handleFolderDelete = (folder: FolderItem): void => {
    if (folder.isDefault) {
      showToast(
        createFailedToast(
          'Default Folders',
          'Default patient folders cannot be deleted.',
        ),
      )
      return
    }
    setDeleteConfirm({
      show: true,
      type: 'folder',
      ids: [folder.id],
      name: folder.name,
    })
  }

  const confirmFolderDelete = useCallback(async (): Promise<void> => {
    const [id] = deleteConfirm.ids
    if (isLocalDraftFolderId(id)) {
      setLocalDraftCustomFolders((p) => p.filter((f) => f.id !== id))
      setLocalDraftMoveModalFolders((p) => p.filter((f) => f.id !== id))
      if (openFolder?.id === id) {
        setOpenFolder(null)
        setSelectedFileIds([])
      }
      setRenamingId((current) => (current === id ? null : current))
      setMoveModalRenamingFolderId((current) =>
        current === id ? null : current,
      )
      setDeleteConfirm({ show: false, type: 'file', ids: [], name: '' })
      return
    }
    const result = await dispatch(deleteFolder(id))
    if (deleteFolder.fulfilled.match(result)) {
      if (openFolder?.id === id) {
        setOpenFolder(null)
        setSelectedFileIds([])
      }
      showToast(
        createSuccessToast('Folder removed', 'The folder has been removed.'),
      )
    } else {
      showToast(
        createFailedToast(
          'Remove folder',
          result.payload || 'Could not remove folder.',
        ),
      )
    }
    setDeleteConfirm({ show: false, type: 'file', ids: [], name: '' })
  }, [deleteConfirm.ids, dispatch, openFolder?.id, showToast])

  const handleDeleteConfirm = useCallback((): void => {
    if (deleteConfirm.type === 'file') {
      confirmFileDelete()
    } else {
      confirmFolderDelete()
    }
  }, [deleteConfirm, confirmFileDelete, confirmFolderDelete])

  const handleMoveConfirm = async (targetFolderId: string): Promise<void> => {
    const activePatientId = selectedPatient?.patient_id
    if (!activePatientId) return
    const { type, itemId, itemIds, folderIds } = moveModal
    const fileIdsToMove =
      type === 'file' ? (itemIds ?? [itemId]).filter(Boolean) : []
    const folderIdsToMove =
      type === 'folder' ? [itemId] : (folderIds ?? []).filter(Boolean)
    const normalizedTarget =
      targetFolderId === ROOT_FOLDER_ID ? null : targetFolderId

    const moveFolderPool = moveModalFolders ?? []
    showLoading({ container: FILES_LOADING_CONTAINER })
    try {
      const result = await dispatch(
        moveFilesAndFoldersAction({
          patientId: activePatientId,
          fileIds: fileIdsToMove,
          folderIds: folderIdsToMove,
          targetFolderId: normalizedTarget,
        }),
      )
      if (moveFilesAndFoldersAction.fulfilled.match(result)) {
        const movedCount =
          result.payload.movedFiles + result.payload.movedFolders
        const targetName =
          targetFolderId === ROOT_FOLDER_ID
            ? 'Main'
            : moveFolderPool.find((f) => f.id === targetFolderId)?.name ||
              'selected folder'
        const openTargetFolder = (): void => {
          if (targetFolderId === ROOT_FOLDER_ID) {
            setOpenFolder(null)
            return
          }
          const folder =
            moveFolderPool.find((f) => f.id === targetFolderId) ||
            mergedCustomFolders.find((f) => f.id === targetFolderId) ||
            defaultFolders.find((f) => f.id === targetFolderId)
          if (folder) {
            setOpenFolder(folder)
          }
        }
        const isSingleFileMove =
          fileIdsToMove.length === 1 && folderIdsToMove.length === 0
        const isMultiFileMove =
          fileIdsToMove.length > 1 && folderIdsToMove.length === 0
        if (isSingleFileMove) {
          const fileName = moveModal.itemNames[0] ?? 'File'
          showToast(
            createFileMovedToast(
              getFileType(fileName),
              targetName,
              openTargetFolder,
            ),
          )
        } else if (isMultiFileMove || movedCount > 1) {
          showToast(createMultipleFilesMovedToast(targetName, openTargetFolder))
        } else {
          const itemLabel = moveModal.itemNames[0] ?? 'Item'
          showToast(
            createFileMovedToast(itemLabel, targetName, openTargetFolder),
          )
        }
        if (previewFile && fileIdsToMove.includes(previewFile.id)) {
          setPreviewFile(null)
        }
        setSelectedFileIds((prev) =>
          prev.filter((id) => !fileIdsToMove.includes(id)),
        )
      } else {
        showToast(
          createFailedToast(
            'Move failed',
            result.payload || 'Could not move items.',
          ),
        )
      }
    } finally {
      hideLoading(FILES_LOADING_CONTAINER)
    }
    setMoveModal({ show: false, itemNames: [], type: 'file', itemId: '' })
    setMoveModalFolders(null)
    setMoveModalBrowseScope(ROOT_FOLDER_ID)
    setMoveModalFoldersLoading(false)
    setLocalDraftMoveModalFolders([])
    setMoveModalRenamingFolderId(null)
  }

  const showMainContent = !isPreviewOpen || !isPreviewFullscreen

  const hasPatientOrSelectionMode =
    effectiveSelectionMode || selectedPatient?.patient_id

  const isListNarrowed =
    searchTerm.trim().length > 0 ||
    (searchFilters.type?.length ?? 0) > 0 ||
    showStarred

  const openFolderHasNoChildren = openFolder
    ? mergedCustomFolders.filter((folder) => folder.parentId === openFolder.id)
        .length === 0
    : false

  const isOpenFolderEmpty =
    Boolean(openFolder) &&
    sortedCurrentFolderFiles.length === 0 &&
    openFolderHasNoChildren

  const uploadDisabled = isOpenFolderEmpty && !isListNarrowed

  return (
    <>
      <div className="files-and-letters-page p-0">
        <FilesAndLettersHeader
          patientName={patientName}
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          searchFilters={searchFilters}
          onFilterChange={setSearchFilters}
          onResetFilters={() => {
            setSearchFilters({})
            setSearchTerm('')
          }}
          typeFilterOptions={FILE_TYPE_FILTER_OPTIONS}
          onCreateFolder={handleCreateFolder}
          onUpload={handleUpload}
          sortConfig={sortConfig}
          onSortChange={setSortConfig}
          isPreviewOpen={isPreviewOpen}
          onPreviewToggle={(on) => {
            setIsPreviewOpen(on)
            if (!on) {
              setPreviewFile(null)
              setIsPreviewFullscreen(false)
            }
          }}
          selectionMode={effectiveSelectionMode}
          selectedCount={selectedFileIds.length}
          onOpen={effectiveSelectionMode ? handleOpenSelection : undefined}
          onCancel={effectiveOnSelectionCancel}
          actionsDisabled={!hasPatientOrSelectionMode}
          uploadDisabled={uploadDisabled}
        />
        <div className="files-and-letters-body">
          {hasPatientOrSelectionMode ? (
            <>
              {showMainContent && (
                <div className="files-and-letters-main-area">
                  <FilesAndLettersFolderGrid
                    storageUsed={formatBytesToLabel(storageUsedBytes)}
                    storageLimit={formatBytesToLabel(storageLimitBytes)}
                    storagePercent={storagePercent}
                    storageLoading={folderState.loading}
                    showStarred={showStarred}
                    onShowStarredChange={setShowStarred}
                    viewMode={viewMode}
                    onViewModeChange={setViewMode}
                    defaultFolders={defaultFolders}
                    customFolders={mergedCustomFolders}
                    onFolderSelect={handleFolderSelect}
                    onCustomFolderSelect={handleCustomFolderSelect}
                    openFolder={openFolder}
                    breadcrumbFolders={workspaceBreadcrumbFolders}
                    onBreadcrumbRootClick={handleBreadcrumbRootClick}
                    files={sortedCurrentFolderFiles}
                    selectedFileIds={selectedFileIds}
                    onClearSelection={() => setSelectedFileIds([])}
                    onFileSelect={handleFileSelect}
                    onFileFavorite={handleFileFavorite}
                    onFileDelete={handleFileDelete}
                    onFileDownload={handleFileDownload}
                    onFileViewFile={handleFileViewFile}
                    showDicomInSelectionBar={showDicomInSelectionBar}
                    onFileViewWithDicom={handleFileViewWithDicom}
                    onFileViewWithDicomMultiple={
                      handleFileViewWithDicomMultiple
                    }
                    onFileNotes={handleFileNotes}
                    onFileDetails={handleFileDetails}
                    onFileMakeCopy={handleFileMakeCopy}
                    onFileRename={handleFileRename}
                    onFileMove={handleFileMove}
                    onFileMoveMultiple={handleFileMoveMultiple}
                    onMoveFilesAndFolders={handleMoveFilesAndFolders}
                    onFileAttachToClaims={handleFileAttachToClaims}
                    onFolderDownloadZip={handleFolderDownloadZip}
                    onFolderDetails={handleFolderDetails}
                    onFolderMakeCopy={handleFolderMakeCopy}
                    onFolderRename={handleFolderRename}
                    onFolderMove={handleFolderMove}
                    onFolderDelete={handleFolderDelete}
                    onFileRenameConfirm={handleFileRenameConfirm}
                    onFolderRenameConfirm={handleFolderRenameConfirm}
                    onFolderRenameEscape={handleFolderRenameEscape}
                    renamingId={renamingId}
                    sampleFiles={sortedSampleFiles}
                    mixedRootItems={mixedRootItems}
                    onUploadRequest={handleUpload}
                    isListNarrowed={isListNarrowed}
                  />
                </div>
              )}

              {isPreviewOpen && (
                <FilePreviewPanel
                  file={previewFile}
                  isFullscreen={isPreviewFullscreen}
                  onToggleFullscreen={() =>
                    setIsPreviewFullscreen((prev) => !prev)
                  }
                  onClose={() => {
                    setIsPreviewOpen(false)
                    setPreviewFile(null)
                    setIsPreviewFullscreen(false)
                  }}
                  onDelete={() => {
                    if (previewFile) handleFileDelete(previewFile.id)
                  }}
                  onDownload={() => {
                    if (previewFile) handleFileDownload(previewFile.id)
                  }}
                  onDetails={() => {
                    if (previewFile) handleFileDetails(previewFile.id)
                  }}
                  onPrev={handlePreviewPrev}
                  onNext={handlePreviewNext}
                  hasPrev={previewFile != null && previewIndex > 0}
                  hasNext={
                    previewFile != null &&
                    previewIndex < allVisibleFiles.length - 1
                  }
                />
              )}
            </>
          ) : (
            <div className="files-and-letters-main-area">
              <div className="files-and-letters-empty-state-wrapper">
                <FilesAndLettersEmptyState
                  variant="noPatient"
                  onSelectPatient={() => dispatch(requestPatientSelection())}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      <MoveFileModal
        show={moveModal.show}
        onHide={() => (
          setMoveModal({
            show: false,
            itemNames: [],
            type: 'file',
            itemId: '',
          }),
          setMoveModalFolders(null),
          setMoveModalBrowseScope(ROOT_FOLDER_ID),
          setMoveModalFoldersLoading(false),
          setLocalDraftMoveModalFolders([]),
          setMoveModalRenamingFolderId(null)
        )}
        itemNames={moveModal.itemNames}
        defaultFolders={moveModalDefaultFolders}
        customFolders={moveModalCustomFolders}
        onMove={handleMoveConfirm}
        onNewFolder={handleCreateFolderInMoveModal}
        excludeFolderIds={
          moveModal.type === 'folder'
            ? [moveModal.itemId]
            : (moveModal.folderIds ?? [])
        }
        showMainFolder={!!openFolder}
        blockedMoveTargetIds={moveModalBlockedMoveTargetIds}
        renamingFolderId={moveModalRenamingFolderId}
        onRenameFolderConfirm={handleMoveModalFolderRenameConfirm}
        onRenameFolderCancel={() => {
          if (
            moveModalRenamingFolderId &&
            isLocalDraftFolderId(moveModalRenamingFolderId)
          ) {
            setLocalDraftMoveModalFolders((p) =>
              p.filter((f) => f.id !== moveModalRenamingFolderId),
            )
          }
          setMoveModalRenamingFolderId(null)
        }}
        foldersLoading={moveModalFoldersLoading}
        onBrowseScopeChange={handleMoveModalBrowseScopeChange}
      />

      <AttachToClaimsModal
        show={!!attachToClaimsFileId}
        onHide={handleAttachToClaimsHide}
        fileId={attachToClaimsFileId}
        fileName={
          allVisibleFiles.find((f) => f.id === attachToClaimsFileId)
            ?.fileName ?? ''
        }
        fileExtension={
          allVisibleFiles
            .find((f) => f.id === attachToClaimsFileId)
            ?.fileName.split('.')
            .pop() ?? ''
        }
        claims={mappedClaimsForModal}
        proceduresByClaimId={mappedProceduresByClaim}
        procedureItemsByCode={mappedProcedureItemsByCode}
        onAttach={handleAttachToClaim}
        onAttachToProcedure={handleAttachToProcedure}
        onAttachToProcedureItem={handleAttachToProcedureItem}
      />

      <FileDetailsModal
        show={!!detailsFile}
        onHide={() => setDetailsFile(null)}
        file={detailsFile}
      />

      <FolderDetailsModal
        show={!!detailsFolder}
        onHide={() => setDetailsFolder(null)}
        folder={detailsFolder}
        files={detailsFolder ? (filesByFolder[detailsFolder.id] ?? []) : []}
      />

      <FilesUploadModal
        show={showUploadModal}
        onHide={() => setShowUploadModal(false)}
        onAttach={handleUploadAttach}
      />

      <FilesDeleteConfirmModal
        show={deleteConfirm.show}
        itemName={deleteConfirm.name}
        itemKind={deleteConfirm.type}
        onCancel={() =>
          setDeleteConfirm({ show: false, type: 'file', ids: [], name: '' })
        }
        onDelete={handleDeleteConfirm}
      />
    </>
  )
}

export default Files
